import { useCallback, useEffect, useRef, useState } from "react";

type Draft = { value: string; base: string };
const draftKey = (id: string) => `unjabbed-about-me-draft:${id}`;
const readDraft = (id: string): Draft | undefined => {
  try {
    const draft = JSON.parse(sessionStorage.getItem(draftKey(id)) || "null");
    if (typeof draft?.value === "string" && typeof draft?.base === "string") return draft;
  } catch { /* Storage can be unavailable. Network saving must still work. */ }
};
const keepDraft = (id: string, value: string, base: string) => {
  if (!id) return;
  try { sessionStorage.setItem(draftKey(id), JSON.stringify({ value, base })); } catch { /* Best effort. */ }
};
const removeDraft = (id: string) => {
  try { sessionStorage.removeItem(draftKey(id)); } catch { /* Best effort. */ }
};

export const useAboutMeAutosave = ({ userId, savedBio, save }: {
  userId: string; savedBio: string; save: (value: string) => Promise<string>;
}) => {
  const [value, setValue] = useState(savedBio);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState("");
  const latest = useRef(savedBio);
  const acknowledged = useRef(savedBio.trim());
  const saveRef = useRef(save);
  saveRef.current = save;
  const currentUserId = useRef(userId);
  currentUserId.current = userId;
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const pending = useRef<Promise<boolean>>();
  const generation = useRef(0);
  const conflict = useRef(false);
  const flushRef = useRef<(explicit?: boolean) => Promise<boolean>>(async () => false);

  const flush = useCallback(async (explicit = false): Promise<boolean> => {
    clearTimeout(timer.current);
    if (!userId || currentUserId.current !== userId || (conflict.current && !explicit)) return false;
    if (pending.current) {
      const ok = await pending.current;
      return ok ? flushRef.current(explicit) : false;
    }
    const text = latest.current.trim();
    if (text === acknowledged.current) {
      removeDraft(userId);
      setStatus("saved");
      return true;
    }
    const currentGeneration = generation.current;
    setStatus("saving");
    setError("");
    const request = (async () => {
      try {
        const saved = await saveRef.current(text);
        if (generation.current !== currentGeneration) return false;
        if (saved !== text) throw new Error("The saved text could not be verified. Please try again.");
        acknowledged.current = saved;
        conflict.current = false;
        if (latest.current.trim() === saved) {
          removeDraft(userId);
          setStatus("saved");
        } else {
          keepDraft(userId, latest.current, saved);
        }
        return true;
      } catch (reason) {
        if (generation.current === currentGeneration) {
          setStatus("error");
          setError(reason instanceof Error ? reason.message : "Your text has not saved. Please try again.");
        }
        return false;
      }
    })();
    pending.current = request;
    const ok = await request;
    if (generation.current !== currentGeneration) return false;
    pending.current = undefined;
    if (ok && latest.current.trim() !== acknowledged.current) return flushRef.current();
    return ok;
  }, [userId]);
  flushRef.current = flush;

  useEffect(() => {
    generation.current += 1;
    pending.current = undefined;
    acknowledged.current = savedBio.trim();
    const draft = userId ? readDraft(userId) : undefined;
    latest.current = draft?.value ?? savedBio;
    setValue(latest.current);
    conflict.current = Boolean(draft && draft.base !== savedBio.trim() && draft.value.trim() !== savedBio.trim());
    setStatus(conflict.current ? "error" : "idle");
    setError(conflict.current ? "A different version was saved. Your draft is shown here; choose Retry to save this version." : "");
    if (draft && !conflict.current) timer.current = setTimeout(() => void flushRef.current(), 700);
    return () => { generation.current += 1; clearTimeout(timer.current); };
    // Only an account change starts a different draft. Own saves must not reset typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!pending.current && latest.current.trim() === acknowledged.current && savedBio.trim() !== acknowledged.current) {
      acknowledged.current = savedBio.trim();
      latest.current = savedBio;
      setValue(savedBio);
    }
  }, [savedBio]);

  useEffect(() => {
    const onHidden = () => { if (document.visibilityState === "hidden") void flushRef.current(); };
    const onLeave = () => { void flushRef.current(); };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onLeave);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onLeave);
    };
  }, []);

  const change = useCallback((text: string) => {
    latest.current = text;
    setValue(text);
    keepDraft(userId, text, acknowledged.current);
    clearTimeout(timer.current);
    if (!conflict.current) {
      setStatus("saving");
      setError("");
      timer.current = setTimeout(() => void flushRef.current(), 700);
    }
  }, [userId]);
  return { value, change, flush, status, error };
};
